import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Clock, Sparkles, Info } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { shouldUseAppleIAP, getDisplayPriceUSD } from '../utils/platform';
import * as iap from '../utils/iapBridge';

// Platform-aware subscribe action.
//   - iOS  : Apple StoreKit 2 IAP (App Review 3.1.1 compliant — no
//            mention of external checkout, no Stripe redirect).
//   - Other: Stripe Checkout redirect (unchanged).
//
// DEFENSE-IN-DEPTH: This function must NEVER navigate to Stripe on iOS.
// Doing so triggers a WKWebView provisional-navigation failure (Stripe's
// domain is not in WKAppBoundDomains), which drops the user onto the
// native launch splash and hangs. So we compute FOUR independent iOS
// signals — if ANY of them fires, we treat the runtime as iOS and refuse
// to fall through to the Stripe path. Each signal is logged to the
// diagnostic panel so a tester can see exactly what tripped the guard.
const startCheckout = async () => {
  // ---- Collect four independent iOS heuristics -------------------------
  const decidedApple = shouldUseAppleIAP();
  const bridgeAvail = iap.isNativeIAPAvailable();
  const uaLower =
    typeof navigator !== 'undefined'
      ? (navigator.userAgent || '').toLowerCase()
      : '';
  const uaMarksIOS =
    uaLower.includes('pwashell') ||
    /iphone|ipad|ipod/.test(uaLower);
  const wkBridgeExists =
    typeof window !== 'undefined' &&
    !!(
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.selahIAP
    );

  const looksLikeIOS =
    decidedApple || bridgeAvail || uaMarksIOS || wkBridgeExists;

  if (typeof window !== 'undefined' && typeof window.__iapDebug === 'function') {
    window.__iapDebug(
      `TrialBanner tap · shouldUseAppleIAP=${decidedApple} bridgeAvail=${bridgeAvail} uaIOS=${uaMarksIOS} wkBridge=${wkBridgeExists}`
    );
  }

  // ---- iOS branch — always use Apple IAP -------------------------------
  if (looksLikeIOS) {
    if (!bridgeAvail) {
      // On iOS but Swift bridge missing → the wrapper build is broken.
      // Do NOT redirect to Stripe (that would trigger the pink launch
      // splash hang). Ask the user to reinstall the latest TestFlight
      // build. Toast auto-dismisses after a few seconds.
      if (typeof window.__iapDebug === 'function') {
        window.__iapDebug('iOS but bridge missing — blocking Stripe fallback');
      }
      toast.error('App Store purchase unavailable', {
        description:
          'Please update to the latest version of the app and try again.',
      });
      return;
    }
    const productId =
      process.env.REACT_APP_APPLE_PRODUCT_ID ||
      'com.selahdownloads.subscription.monthly';
    if (typeof window.__iapDebug === 'function') {
      window.__iapDebug(`Calling iap.purchase(${productId})`);
    }
    const result = await iap.purchase(productId);
    if (typeof window.__iapDebug === 'function') {
      try {
        window.__iapDebug(
          `iap.purchase resolved · ok=${result?.ok} cancelled=${result?.cancelled} err=${result?.error || 'none'} hasJWS=${!!result?.signedTransaction}`
        );
      } catch (_e) { /* ignore */ }
    }
    if (!result.ok) {
      if (result.cancelled) return; // user dismissed the Apple sheet
      toast.error('Purchase could not complete', {
        description: result.error || 'Please try again in a moment.',
      });
      return;
    }
    try {
      if (typeof window.__iapDebug === 'function') {
        window.__iapDebug('POST /api/apple/verify-transaction …');
      }
      const verify = await api.verifyAppleTransaction({
        signed_transaction: result.signedTransaction,
        signed_renewal_info: result.signedRenewalInfo || null,
      });
      if (typeof window.__iapDebug === 'function') {
        try {
          window.__iapDebug(
            `verify response · is_active=${verify?.is_active} provider=${verify?.provider || 'n/a'} err=${verify?.error || 'none'}`
          );
        } catch (_e) { /* ignore */ }
      }
      if (verify?.is_active) {
        toast.success('Welcome to Selah — subscription active.');
        setTimeout(() => window.location.reload(), 900);
      } else {
        toast.error('Purchase completed but activation failed', {
          description: 'Please contact support@selahdownloads.com.',
        });
      }
    } catch (e) {
      toast.error('Activation failed', { description: e?.message });
    }
    return;
  }

  // ---- Non-iOS branch — Stripe Checkout redirect -----------------------
  try {
    if (typeof window.__iapDebug === 'function') {
      window.__iapDebug('Non-iOS — creating Stripe checkout session');
    }
    const { url } = await api.createStripeCheckoutSession(window.location.origin);
    if (url) window.location.href = url;
  } catch (e) {
    toast.error('Could not start checkout', { description: e?.message });
  }
};

const TrialBanner = () => {
  const { user } = useAuth();
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchTrialStatus();
    }
  }, [user]);

  const fetchTrialStatus = async () => {
    try {
      const status = await api.get('/api/subscription/status');
      setTrialStatus(status);
    } catch (error) {
      console.error('Failed to fetch trial status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !trialStatus) return null;

  // Don't show if user has active subscription
  if (trialStatus.subscription?.is_active) return null;

  // Show trial banner with detailed info
  if (trialStatus.trial?.is_trial && trialStatus.days_remaining > 0) {
    return (
      <div className="mb-6">
        <Card className="glass-card p-5 border-[var(--sd-primary)]">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Clock size={20} className="text-[var(--sd-primary)]" />
                <div>
                  <p className="text-sm font-bold text-[#1C1C1C]">
                    Free Trial: {trialStatus.days_remaining} {trialStatus.days_remaining === 1 ? 'day' : 'days'} remaining
                  </p>
                  <p className="text-xs text-[#1C1C1C] font-medium">
                    Then ${getDisplayPriceUSD().toFixed(2)}/mo · Cancel anytime before day 7
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2 text-xs text-[var(--sd-muted-fg)] bg-[rgba(245,245,245,0.05)] px-3 py-2 rounded-lg border border-[rgba(245,245,245,0.08)]">
                <Info size={14} className="flex-shrink-0 mt-0.5" />
                <p>
                  Don't worry! Your downloads are saved. You can access them anytime if you choose to resubscribe later.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-[var(--sd-primary)] hover:bg-[#D7A1A8] text-[var(--sd-primary-fg)] flex-shrink-0"
              onClick={startCheckout}
            >
              <Sparkles size={16} className="mr-2" />
              Subscribe Now
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Trial expired - emphasize downloads are saved
  if (trialStatus.needs_subscription) {
    return (
      <div className="mb-6">
        <Card className="glass-card p-5 border-red-500/50 bg-red-500/10">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Clock size={20} className="text-red-400" />
                <div>
                  <p className="text-sm font-semibold text-red-400">
                    Your free trial has ended
                  </p>
                  <p className="text-xs text-[var(--sd-muted-fg)]">
                    Continue for ${getDisplayPriceUSD().toFixed(2)}/mo · Cancel anytime
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2 text-xs text-[var(--sd-muted-fg)] bg-[rgba(245,245,245,0.05)] px-3 py-2 rounded-lg border border-[rgba(245,245,245,0.08)]">
                <Info size={14} className="flex-shrink-0 mt-0.5 text-[var(--sd-primary)]" />
                <p>
                  <span className="font-medium text-[var(--sd-primary)]">Good news:</span> All your previous downloads are safely saved! Subscribe to access them and continue your spiritual journey.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-[var(--sd-primary)] hover:bg-[#D7A1A8] text-[var(--sd-primary-fg)] flex-shrink-0"
              onClick={startCheckout}
            >
              <Sparkles size={16} className="mr-2" />
              Subscribe Now
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return null;
};

export default TrialBanner;
